import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import RoleService from '../../services/user-role/role-service';
import { CreateRolesDTO, UpdateRolesDTO } from '../../models/user-role/role-model';

interface NewRoleDialogProps {
    visible: boolean;
    onHide: () => void;
    onSaveSuccess: () => void;
    editMode: boolean;
    initialData?: { roleId: number, name: string, description?: string };
    toast: React.RefObject<Toast>;
}

const NewRoleDialog: React.FC<NewRoleDialogProps> = ({ visible, onHide, onSaveSuccess, editMode, initialData, toast }) => {
    const [roleDetail, setRoleDetail] = useState<CreateRolesDTO | UpdateRolesDTO>({ name: '', description: '' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (visible) {
            setSubmitted(false);
            if (editMode && initialData) {
                setRoleDetail({ roleId: initialData.roleId, name: initialData.name, description: initialData.description || '' } as UpdateRolesDTO);
            } else {
                setRoleDetail({ name: '', description: '' });
            }
        }
    }, [visible, editMode, initialData]);

    const handleSave = async () => {
        setSubmitted(true);

        if (roleDetail.name.trim()) {
            setLoading(true);
            try {
                if (editMode && 'roleId' in roleDetail) {
                    const response = await RoleService.updateRole(roleDetail as UpdateRolesDTO);
                    if (response.isSuccess) {
                        toast.current?.show({ severity: 'success', summary: 'Successful', detail: 'Role Updated' });
                        onSaveSuccess();
                        onHide();
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Error', detail: response.message || 'Failed to update role' });
                    }
                } else {
                    const response = await RoleService.createRole(roleDetail as CreateRolesDTO);
                    if (response.isSuccess) {
                        toast.current?.show({ severity: 'success', summary: 'Successful', detail: 'Role Created' });
                        onSaveSuccess();
                        onHide();
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Error', detail: response.message || 'Failed to create role' });
                    }
                }
            } catch (error) {
                console.error(error);
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'An error occurred' });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: 'name' | 'description') => {
        let val = e.target.value;

        if (val.length === 1 && val === ' ') {
            return;
        }

        val = val.replace(/\s\s+/g, ' ');

        if (val.startsWith(' ')) {
            val = val.trimStart();
        }

        setRoleDetail({ ...roleDetail, [field]: val });
    };

    return (
        <Dialog
            visible={visible}
            header={editMode ? "Role Details" : "New Role"}
            modal
            className="role-dialog p-fluid w-30rem"
            onHide={onHide}
        >
            <div className="field">
                <label htmlFor="name">Name <span className="text-red-500">*</span></label>
                <InputText
                    id="name"
                    value={roleDetail.name}
                    onChange={(e) => handleInputChange(e, 'name')}
                    required
                    autoFocus
                    placeholder="Enter role name"
                    className={submitted && !roleDetail.name ? 'p-invalid' : ''}
                />
                {submitted && !roleDetail.name && <small className="p-error">Name is required.</small>}
            </div>
            <div className="field">
                <label htmlFor="description">Description</label>
                <InputTextarea
                    id="description"
                    value={roleDetail.description || ''}
                    onChange={(e) => handleInputChange(e, 'description')}
                    rows={3}
                    cols={20}
                    placeholder="Enter role description (optional)"
                />
            </div>

            <div className="flex justify-content-end mt-4">
                <Button
                    label="Save"
                    icon="pi pi-check"
                    onClick={handleSave}
                    loading={loading}
                    className="p-button-sm w-auto pr-2"
                />
            </div>
        </Dialog>
    );
};

export default NewRoleDialog;
